import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  BarChart3, Users, TrendingDown, MapPin, RefreshCw, AlertTriangle,
  Building, Heart, Target, Zap, ArrowDown, ArrowUp, Activity,
  PieChart, Layers, Filter, Download
} from "lucide-react";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart as RechartsPie, Pie, Cell, Legend, FunnelChart, Funnel, LabelList,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar
} from "recharts";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const COLORS = ['#ccd500', '#384451', '#60a5fa', '#f59e0b', '#10b981', '#8b5cf6', '#ec4899'];

// Dashboard Summary Component
const AnalyticsSummary = ({ data }) => {
  if (!data) return null;

  const summaryCards = [
    { label: "Total Usuarios", value: data.summary?.total_users || 0, icon: Users, color: "text-blue-500" },
    { label: "Propiedades", value: data.summary?.total_properties || 0, icon: Building, color: "text-green-500" },
    { label: "Matches", value: data.summary?.total_matches || 0, icon: Heart, color: "text-rose-500" },
    { label: "Like Rate", value: `${data.engagement?.like_rate || 0}%`, icon: TrendingDown, color: "text-amber-500" }
  ];

  return (
    <div className="space-y-6" data-testid="analytics-summary">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {summaryCards.map((card, idx) => {
          const Icon = card.icon;
          return (
            <Card key={idx} className="border-0 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[#384451]/60">{card.label}</p>
                    <p className="text-2xl font-bold text-[#384451]">{card.value}</p>
                  </div>
                  <Icon className={`w-8 h-8 ${card.color}`} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Conversion Funnel */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-[#384451]">Embudo de Conversión</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              {[
                { label: "Usuarios Registrados", value: data.funnel?.registered_users || 0, color: "bg-blue-500" },
                { label: "Perfiles Completados", value: data.funnel?.completed_profiles || 0, color: "bg-green-500" },
                { label: "Swipers Activos", value: data.funnel?.active_swipers || 0, color: "bg-amber-500" },
                { label: "Total Matches", value: data.funnel?.total_matches || 0, color: "bg-rose-500" }
              ].map((stage, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${stage.color}`}></div>
                  <span className="text-sm text-[#384451]/70 flex-1">{stage.label}</span>
                  <span className="font-bold text-[#384451]">{stage.value}</span>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-[#384451]">Tasas de Conversión</h4>
              {[
                { label: "Registro → Perfil", value: data.conversion_rates?.registration_to_profile },
                { label: "Perfil → Match", value: data.conversion_rates?.profile_to_match },
                { label: "Conversión Total", value: data.conversion_rates?.overall_conversion }
              ].map((rate, idx) => (
                <div key={idx} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#384451]/60">{rate.label}</span>
                    <span className="font-medium text-[#384451]">{rate.value}%</span>
                  </div>
                  <Progress value={rate.value} className="h-2" />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Platform Health */}
      <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
        <Activity className={`w-6 h-6 ${
          data.health_indicators?.platform_status === 'healthy' ? 'text-green-500' :
          data.health_indicators?.platform_status === 'growing' ? 'text-amber-500' : 'text-red-500'
        }`} />
        <div>
          <div className="flex items-center gap-2 font-medium text-[#384451]">
            <span>Estado de la Plataforma:</span>
            <Badge className={
              data.health_indicators?.platform_status === 'healthy' ? 'bg-green-500' :
              data.health_indicators?.platform_status === 'growing' ? 'bg-amber-500' : 'bg-red-500'
            }>
              {data.health_indicators?.platform_status === 'healthy' ? 'Saludable' :
               data.health_indicators?.platform_status === 'growing' ? 'En Crecimiento' : 'Necesita Atención'}
            </Badge>
          </div>
          <p className="text-sm text-[#384451]/60">
            {data.recent_activity?.new_users_7d || 0} nuevos usuarios y {data.recent_activity?.new_matches_7d || 0} matches en los últimos 7 días
          </p>
        </div>
      </div>
    </div>
  );
};

// Abandonment Analytics Component
const AbandonmentAnalytics = ({ data }) => {
  if (!data) return null;

  const chartData = data.abandonment_by_wave?.map(wave => ({
    name: `Ola ${wave.wave}`,
    category: wave.category,
    completed: wave.completion_rate,
    abandoned: wave.abandonment_rate
  })) || [];

  return (
    <div className="space-y-6" data-testid="abandonment-analytics">
      {/* Overview */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-[#384451]">{data.total_users_analyzed}</p>
            <p className="text-sm text-[#384451]/60">Usuarios Analizados</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-green-500">{data.insights?.overall_completion_rate}%</p>
            <p className="text-sm text-[#384451]/60">Tasa de Completitud</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-red-500">{data.insights?.highest_abandonment_wave}</p>
            <p className="text-sm text-[#384451]/60">Mayor Abandono</p>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-[#384451]">Abandono por Ola del Onboarding</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" domain={[0, 100]} />
              <YAxis dataKey="name" type="category" width={80} />
              <Tooltip formatter={(value) => `${value}%`} />
              <Legend />
              <Bar dataKey="completed" name="Completado" fill="#22c55e" stackId="a" />
              <Bar dataKey="abandoned" name="Abandonado" fill="#ef4444" stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Top Abandoned Questions */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-[#384451]">Preguntas con Mayor Abandono</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {data.abandonment_by_wave?.map((wave, idx) => (
              <div key={idx}>
                <h4 className="text-sm font-medium text-[#384451] mb-2">
                  {wave.wave_name} - {wave.category}
                </h4>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {wave.top_abandoned_questions?.slice(0, 3).map((q, qIdx) => (
                    <div key={qIdx} className="p-2 bg-red-50 rounded-lg text-sm">
                      <p className="font-medium text-[#384451]">{q.name}</p>
                      <p className="text-red-600">{q.skip_rate}% omitido</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Buyer Segments Component
const BuyerSegments = ({ data }) => {
  if (!data) return null;

  const pieData = data.segments?.filter(s => s.user_count > 0).map(seg => ({
    name: seg.name,
    value: seg.user_count,
    icon: seg.icon
  })) || [];

  return (
    <div className="space-y-6" data-testid="buyer-segments">
      {/* Overview */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-[#384451]">{data.total_profiles_analyzed}</p>
            <p className="text-sm text-[#384451]/60">Perfiles Analizados</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-primary">{data.total_classified}</p>
            <p className="text-sm text-[#384451]/60">Usuarios Clasificados</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-[#384451]">{data.segments?.length || 0}</p>
            <p className="text-sm text-[#384451]/60">Segmentos Definidos</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[#384451]">Distribución de Segmentos</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <RechartsPie>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </RechartsPie>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Segments List */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-[#384451]">Detalle de Segmentos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[300px] overflow-y-auto">
              {data.segments?.map((seg, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">{seg.icon}</span>
                    <div className="flex-1">
                      <p className="font-medium text-[#384451]">{seg.name}</p>
                      <p className="text-xs text-[#384451]/60">{seg.description}</p>
                    </div>
                    <Badge variant="outline">{seg.user_count} usuarios</Badge>
                  </div>
                  {seg.top_zones?.length > 0 && (
                    <div className="flex gap-2 flex-wrap mt-2">
                      {seg.top_zones.map((zone, zIdx) => (
                        <Badge key={zIdx} variant="secondary" className="text-xs">
                          <MapPin className="w-3 h-3 mr-1" />
                          {zone.zone}: {zone.count}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Market Comparison Component
const MarketComparison = ({ data }) => {
  if (!data) return null;

  const chartData = data.zones?.slice(0, 8).map(zone => ({
    name: zone.zone.length > 12 ? zone.zone.substring(0, 12) + '...' : zone.zone,
    fullName: zone.zone,
    properties: zone.metrics?.property_count || 0,
    likeRate: zone.engagement?.like_rate || 0,
    pricePerM2: zone.metrics?.price_per_m2 || 0
  })) || [];

  return (
    <div className="space-y-6" data-testid="market-comparison">
      {/* Insights */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <TrendingDown className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-sm text-green-700">Zona Más Caliente</p>
                <p className="text-xl font-bold text-green-800">{data.insights?.hottest_zone || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-amber-50 to-amber-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Building className="w-8 h-8 text-amber-600" />
              <div>
                <p className="text-sm text-amber-700">Más Cara ($/m²)</p>
                <p className="text-xl font-bold text-amber-800">{data.insights?.most_expensive_zone || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Activity className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-sm text-blue-700">Más Activa</p>
                <p className="text-xl font-bold text-blue-800">{data.insights?.most_active_zone || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-[#384451]">Comparativa por Zona</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis yAxisId="left" orientation="left" stroke="#ccd500" />
              <YAxis yAxisId="right" orientation="right" stroke="#384451" />
              <Tooltip 
                formatter={(value, name) => {
                  if (name === 'likeRate') return [`${value}%`, 'Like Rate'];
                  if (name === 'properties') return [value, 'Propiedades'];
                  return [value, name];
                }}
              />
              <Legend />
              <Bar yAxisId="left" dataKey="properties" name="Propiedades" fill="#ccd500" />
              <Bar yAxisId="right" dataKey="likeRate" name="Like Rate %" fill="#384451" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Zone Details Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-[#384451]">Detalle por Zona</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2 text-[#384451]/60">Zona</th>
                  <th className="text-center p-2 text-[#384451]/60">Props</th>
                  <th className="text-center p-2 text-[#384451]/60">Precio Prom</th>
                  <th className="text-center p-2 text-[#384451]/60">$/m²</th>
                  <th className="text-center p-2 text-[#384451]/60">Vistas</th>
                  <th className="text-center p-2 text-[#384451]/60">Like Rate</th>
                  <th className="text-center p-2 text-[#384451]/60">Estado</th>
                </tr>
              </thead>
              <tbody>
                {data.zones?.map((zone, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="p-2 font-medium text-[#384451]">{zone.zone}</td>
                    <td className="p-2 text-center">{zone.metrics?.property_count}</td>
                    <td className="p-2 text-center">{zone.metrics?.avg_price_formatted}</td>
                    <td className="p-2 text-center">{zone.metrics?.price_per_m2_formatted}</td>
                    <td className="p-2 text-center">{zone.engagement?.total_views}</td>
                    <td className="p-2 text-center">{zone.engagement?.like_rate}%</td>
                    <td className="p-2 text-center">
                      <Badge className={
                        zone.market_health === 'hot' ? 'bg-green-500' :
                        zone.market_health === 'warm' ? 'bg-amber-500' : 'bg-blue-500'
                      }>
                        {zone.market_health === 'hot' ? '🔥 Hot' :
                         zone.market_health === 'warm' ? '☀️ Warm' : '❄️ Cool'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Analytics Dashboard Component
export const AdvancedAnalytics = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [abandonmentData, setAbandonmentData] = useState(null);
  const [segmentsData, setSegmentsData] = useState(null);
  const [marketData, setMarketData] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [dashboard, abandonment, segments, market] = await Promise.all([
        axios.get(`${API}/analytics/dashboard`),
        axios.get(`${API}/analytics/abandonment`),
        axios.get(`${API}/analytics/buyer-segments`),
        axios.get(`${API}/analytics/market-comparison`)
      ]);
      
      setDashboardData(dashboard.data);
      setAbandonmentData(abandonment.data);
      setSegmentsData(segments.data);
      setMarketData(market.data);
    } catch (error) {
      toast.error("Error al cargar analytics");
      console.error("Analytics error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="w-full">
        <CardContent className="p-8 text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#384451]/70">Cargando Analytics Avanzados...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="advanced-analytics">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#384451]">Analytics Avanzados</h2>
          <p className="text-[#384451]/60">Análisis profundo de la plataforma Findperty</p>
        </div>
        <Button variant="outline" onClick={fetchAllData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="dashboard" className="text-xs md:text-sm">
            <BarChart3 className="w-4 h-4 mr-1 hidden md:inline" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="abandonment" className="text-xs md:text-sm">
            <TrendingDown className="w-4 h-4 mr-1 hidden md:inline" />
            Abandono
          </TabsTrigger>
          <TabsTrigger value="segments" className="text-xs md:text-sm">
            <Users className="w-4 h-4 mr-1 hidden md:inline" />
            Segmentos
          </TabsTrigger>
          <TabsTrigger value="market" className="text-xs md:text-sm">
            <MapPin className="w-4 h-4 mr-1 hidden md:inline" />
            Mercado
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <AnalyticsSummary data={dashboardData} />
        </TabsContent>

        <TabsContent value="abandonment">
          <AbandonmentAnalytics data={abandonmentData} />
        </TabsContent>

        <TabsContent value="segments">
          <BuyerSegments data={segmentsData} />
        </TabsContent>

        <TabsContent value="market">
          <MarketComparison data={marketData} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdvancedAnalytics;
